import { motion } from "framer-motion";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { QuoteForm } from "../components/site/QuoteForm";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { SERVICES, CLIENT_LOCATIONS, GALLERY, COMPANY, FAQS } from "../lib/data";
import {
  Building2, Home as HomeIcon, PenTool, Truck, Flame, Layers, Sparkles,
  SignpostBig, ArrowRight, Phone, Mail, MapPin, ShieldCheck, Award,
  Users, ThumbsUp, Boxes, CheckCircle2,
} from "lucide-react";

const ICONS = { Building2, Home: HomeIcon, PenTool, Truck, Flame, Layers, Sparkles, SignpostBig };

const HERO_BG = "https://images.unsplash.com/photo-1621121013599-c94f85975cfd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const fade = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } }),
};

const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

/* ---------------- Hero ---------------- */
const Hero = () => (
  <section id="top" className="relative min-h-screen flex items-center" data-testid="hero-section">
    <div className="absolute inset-0">
      <img src={HERO_BG} alt="Industrial cement plant" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[#0A3B29]/80" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/40" />
    </div>
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-28 pb-16 w-full">
      <motion.div initial="hidden" animate="show" variants={fade} custom={0}>
        <span className="overline text-amber-400">Industrial Painting & Coating Contractor &bull; Since 2013</span>
      </motion.div>
      <motion.h1 initial="hidden" animate="show" variants={fade} custom={1}
        className="font-display font-extrabold text-white tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mt-6 max-w-4xl">
        Painting Excellence,<br /><span className="text-amber-400">Redefined.</span>
      </motion.h1>
      <motion.p initial="hidden" animate="show" variants={fade} custom={2}
        className="text-zinc-200 text-lg mt-8 max-w-2xl leading-relaxed">
        Trusted by India&apos;s largest cement and infrastructure firms. We deliver
        structural painting, epoxy coatings, heat-resistant finishes and thermoplastic
        road marking at industrial scale — with {COMPANY.experience} years of proven craftsmanship.
      </motion.p>
      <motion.div initial="hidden" animate="show" variants={fade} custom={3} className="flex flex-wrap gap-4 mt-10">
        <Button data-testid="hero-quote-btn" onClick={() => scrollTo("#contact")}
          className="rounded-none h-14 px-8 text-base bg-accent hover:bg-accent/90 text-white font-semibold group">
          Request a Quote <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
        </Button>
        <Button data-testid="hero-services-btn" onClick={() => scrollTo("#services")} variant="outline"
          className="rounded-none h-14 px-8 text-base bg-transparent border-white/40 text-white hover:bg-white hover:text-zinc-900 font-semibold">
          Explore Services
        </Button>
      </motion.div>
      <motion.div initial="hidden" animate="show" variants={fade} custom={4}
        className="flex flex-wrap gap-8 mt-16 border-t border-white/20 pt-8">
        {[["12+", "Years Experience"], ["8+", "Cement Majors Served"], ["1000+", "Projects Delivered"], ["8", "Specialised Services"]].map(([n, l]) => (
          <div key={l}>
            <div className="font-display font-extrabold text-3xl text-amber-400">{n}</div>
            <div className="overline text-white/70 mt-1 text-[10px]">{l}</div>
          </div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ---------------- Trusted marquee ---------------- */
const Trusted = () => {
  const items = [...CLIENT_LOCATIONS, ...CLIENT_LOCATIONS];
  return (
    <section id="clients" className="bg-white border-y border-zinc-200 py-14 overflow-hidden" data-testid="trusted-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 mb-8">
        <span className="overline text-zinc-500">Trusted by industry leaders</span>
      </div>
      <div className="marquee-track relative">
        <div className="flex gap-4 w-max animate-marquee">
          {items.map((c, i) => (
            <div key={i} className="flex items-center gap-3 border border-zinc-200 bg-white px-6 py-4 min-w-[280px]">
              {c.logo ? (
                <div className="w-14 h-14 bg-white flex items-center justify-center shrink-0 p-1">
                  <img src={c.logo} alt={c.name} className="max-w-full max-h-full object-contain" />
                </div>
              ) : (
                <div className="w-12 h-12 bg-primary text-white flex items-center justify-center font-display font-extrabold shrink-0">
                  {c.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                </div>
              )}
              <div>
                <div className="font-semibold text-sm text-zinc-900 leading-tight">{c.name}</div>
                <div className="text-xs text-zinc-500">{c.site}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Services ---------------- */
const Services = () => (
  <section id="services" className="bg-zinc-50 py-24" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="max-w-2xl">
        <span className="overline text-accent">What we do</span>
        <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-zinc-900 mt-4">
          Comprehensive painting services under one roof.
        </h2>
        <p className="text-zinc-600 mt-5 text-lg">
          From towering industrial structures to precision road marking — every project is
          engineered for durability and finished to perfection.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 mt-14">
        {SERVICES.map((s, i) => {
          const Icon = ICONS[s.icon] || Layers;
          return (
            <motion.div key={s.id} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={i % 4}
              data-testid={`service-card-${s.id}`}
              className="relative overflow-hidden group cursor-default min-h-[320px] flex flex-col justify-end">
              <img src={s.bg} alt={s.title} loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/95 via-primary/70 to-primary/25 transition-opacity duration-300 group-hover:from-primary group-hover:via-primary/85" />
              <div className="relative p-8">
                <div className="w-12 h-12 border border-white/40 bg-white/10 backdrop-blur-sm flex items-center justify-center group-hover:border-amber-400 transition-colors">
                  <Icon className="w-6 h-6 text-amber-400" />
                </div>
                <h3 className="font-display font-bold text-xl mt-6 text-white">{s.title}</h3>
                <p className="text-sm text-zinc-100/90 mt-3 leading-relaxed">{s.desc}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------------- Gallery ---------------- */
const Gallery = () => {
  const cats = [
    { key: "structure", label: "Structural" },
    { key: "building", label: "Building" },
    { key: "vehicle", label: "Vehicle & Ads" },
    { key: "writing", label: "Painting & Writing" },
    { key: "heat", label: "Heat Resistance" },
    { key: "epoxy", label: "Epoxy & Marble Coating" },
    { key: "thermoplastic", label: "Thermoplastic Road Marking" },
  ];
  return (
    <section id="work" className="bg-white py-24" data-testid="work-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <span className="overline text-accent">Portfolio</span>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-zinc-900 mt-4">
              Our work, on the ground.
            </h2>
          </div>
          <p className="text-zinc-500 max-w-sm">Real projects executed across cement plants, commercial buildings and advertisement fleets.</p>
        </div>

        <Tabs defaultValue="structure" className="mt-12">
          <TabsList className="rounded-none bg-zinc-100 p-1 h-auto flex-wrap">
            {cats.map((c) => (
              <TabsTrigger key={c.key} value={c.key} data-testid={`gallery-tab-${c.key}`}
                className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-2.5 font-semibold">
                {c.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {cats.map((c) => (
            <TabsContent key={c.key} value={c.key} className="mt-8">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {GALLERY[c.key].map((src, i) => {
                  const isWide = /(eq1|pu1|scaff1|heat1)\.jpg$/.test(src);
                  return (
                  <motion.div key={src} initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 8) * 0.04 }}
                    className={`relative overflow-hidden bg-zinc-100 group ${isWide ? "col-span-2 md:col-span-3 lg:col-span-4 aspect-[16/6]" : "aspect-[4/3]"}`}
                    data-testid={`gallery-img-${c.key}-${i}`}>
                    <img src={src} alt={`${c.label} project ${i + 1}`} loading="lazy"
                      className={`w-full h-full ${isWide ? "object-contain" : "object-cover group-hover:scale-110"} transition-transform duration-500`} />
                    <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/30 transition-colors" />
                  </motion.div>
                  );
                })}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};

/* ---------------- Why Choose Us ---------------- */
const Why = () => {
  const points = [
    { icon: Award, t: "Proven Expertise", d: "12+ years of hands-on industrial painting mastery." },
    { icon: ShieldCheck, t: "Premium Quality", d: "Only the best materials and techniques, built to last." },
    { icon: Users, t: "Professional Team", d: "Skilled painters equipped with the latest tools." },
    { icon: ThumbsUp, t: "Customer Satisfaction", d: "Client-centric delivery tailored to your vision." },
    { icon: Boxes, t: "Full Versatility", d: "Residential, commercial, industrial & vehicles." },
    { icon: CheckCircle2, t: "Comprehensive Services", d: "All your painting needs under one roof." },
  ];
  return (
    <section id="why" className="bg-[#18181B] text-white py-24" data-testid="why-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="overline text-amber-400">Why choose us</span>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight mt-4">
              Built on trust. Delivered at scale.
            </h2>
            <p className="text-zinc-400 mt-6 text-lg leading-relaxed">
              At Pooja Painting Work, we believe in a client-centric approach — working closely
              with you to understand your needs and delivering superior results, efficiently and effectively.
            </p>
            <div className="grid grid-cols-2 gap-8 mt-12 border-t border-zinc-800 pt-10">
              <div>
                <div className="font-display font-extrabold text-5xl text-amber-400">{COMPANY.experience}+</div>
                <div className="overline text-zinc-500 mt-2">Years of Excellence</div>
              </div>
              <div>
                <div className="font-display font-extrabold text-5xl text-amber-400">8+</div>
                <div className="overline text-zinc-500 mt-2">Cement Majors Served</div>
              </div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-px bg-zinc-800 border border-zinc-800">
            {points.map((p) => (
              <div key={p.t} className="bg-[#18181B] p-7 hover:bg-zinc-900 transition-colors" data-testid={`why-point-${p.t.toLowerCase().replace(/\s/g, "-")}`}>
                <p.icon className="w-7 h-7 text-amber-400" />
                <h3 className="font-display font-bold text-lg mt-4">{p.t}</h3>
                <p className="text-sm text-zinc-400 mt-2">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- FAQ (AEO / GEO) ---------------- */
const FAQ = () => (
  <section id="faq" className="bg-white py-24 border-t border-zinc-200" data-testid="faq-section">
    <div className="max-w-4xl mx-auto px-5 sm:px-8">
      <div className="text-center max-w-2xl mx-auto">
        <span className="overline text-accent">Frequently asked questions</span>
        <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-zinc-900 mt-4">
          Everything you need to know.
        </h2>
        <p className="text-zinc-600 mt-5 text-lg">
          Answers to the most common questions about our industrial painting and coating services.
        </p>
      </div>
      <Accordion type="single" collapsible className="mt-12" data-testid="faq-accordion">
        {FAQS.map((f, i) => (
          <AccordionItem key={i} value={`faq-${i}`} className="border-b border-zinc-200" data-testid={`faq-item-${i}`}>
            <AccordionTrigger className="text-left font-display font-bold text-lg text-zinc-900 hover:text-accent py-6">
              {f.q}
            </AccordionTrigger>
            <AccordionContent className="text-zinc-600 text-base leading-relaxed pb-6">
              {f.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

/* ---------------- Contact / Quote ---------------- */
const Contact = () => (
  <section id="contact" className="bg-zinc-50 py-24" data-testid="contact-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16">
      <div>
        <span className="overline text-accent">Get a quote</span>
        <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-zinc-900 mt-4">
          Let&apos;s bring colour & protection to your project.
        </h2>
        <p className="text-zinc-600 mt-5 text-lg">
          Share your requirements and our team will get back with a tailored quotation.
          For urgent enquiries, call us directly.
        </p>
        <div className="mt-10 space-y-5">
          {COMPANY.phones.map((p) => (
            <a key={p} href={`tel:${p.replace(/\s/g, "")}`} data-testid={`contact-phone-${p.replace(/\s/g, "")}`}
              className="flex items-center gap-4 group">
              <span className="w-11 h-11 bg-primary text-white flex items-center justify-center group-hover:bg-accent transition-colors"><Phone className="w-5 h-5" /></span>
              <span className="font-semibold text-zinc-900">{p}</span>
            </a>
          ))}
          {COMPANY.emails.map((e) => (
            <a key={e} href={`mailto:${e}`} data-testid={`contact-email-${e}`} className="flex items-center gap-4 group">
              <span className="w-11 h-11 bg-primary text-white flex items-center justify-center group-hover:bg-accent transition-colors"><Mail className="w-5 h-5" /></span>
              <span className="font-semibold text-zinc-900 break-all">{e}</span>
            </a>
          ))}
          <div className="flex items-start gap-4">
            <span className="w-11 h-11 bg-primary text-white flex items-center justify-center shrink-0"><MapPin className="w-5 h-5" /></span>
            <div>
              <span className="font-semibold text-zinc-900 block">{COMPANY.address}</span>
              <span className="font-mono-a text-xs text-zinc-500">GSTIN: {COMPANY.gstin}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white border border-zinc-200 p-8 sm:p-10">
        <h3 className="font-display font-bold text-2xl text-zinc-900 mb-6">Request a Quote</h3>
        <QuoteForm />
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div className="App bg-white">
      <Header />
      <Hero />
      <Trusted />
      <Services />
      <Gallery />
      <Why />
      <FAQ />
      <Contact />
      <Footer />
    </div>
  );
}
